import React, { useState, useEffect, useCallback } from 'react';
import { useAlert } from 'react-alert';
import Cookies from 'js-cookie';
import axios from 'axios';
import regenerateToken from '../../regenerateToken';
import Loader from '../../loader/loader';
import AppLayout from '../../layout/AppLayout';
import Dashboard from './Dashboard';
import EditorWorkspace from './EditorWorkspace';
import handleExport from '../exportProfileContent';
import { PROFILE_TEMPLATE, DEFAULT_LANG_OBJ } from './constants';
import { buildRequestConfig } from '../../../utils/apiHelper';
import { isAdmin as checkAdmin } from '../../../utils/authUtils';
import './redesign.css';

const ProfileContentManager = ({ version, setEditVersion }) => {
    const alert = useAlert();

    // Data State
    const [profileContent, setProfileContent] = useState({});
    const [loading, setLoading] = useState(true);
    const [saveLoading, setSaveLoading] = useState(false);

    // Navigation State
    const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard' | 'editor'
    const [activeProfile, setActiveProfile] = useState(null);
    const [activeReportType, setActiveReportType] = useState('advanced');
    const [activeSection, setActiveSection] = useState('about');
    const [activeLang, setActiveLang] = useState('en');

    // Languages state (can be extended dynamically)
    const [availableLang, setAvailableLang] = useState(['en', 'hi', 'ar', 'cs']);
    const [availableLangObj, setAvailableLangObj] = useState({ en: '', hi: '', ar: '', cs: '' });
    const [availableLangArray, setAvailableLangArray] = useState({ en: [], hi: [], ar: [], cs: [] });

    // Version management
    const storedVersions = JSON.parse(localStorage.getItem('versionData') || '[]');
    const allVersions = storedVersions?.length
        ? [...storedVersions.map(v => v.name), 'live']
        : ['live'];

    // =====================
    // API CALLS (Preserved from original)
    // =====================

    const getExistingProfileContent = useCallback(async () => {
        if (checkAdmin()) {
            return await axios({
                ...buildRequestConfig({
                    method: 'GET',
                    url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/content/profile',
                    extraParams: { version },
                }),
                validateStatus: (status) => status >= 200 && status <= 401,
            });
        }
        const idToken = Cookies.get('idToken');
        const configBody = {
            headers: { authorization: idToken },
            method: 'GET',
            url: `https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/content/profile?version=${version}`,
            validateStatus: (status) => status >= 200 && status <= 401
        };
        return await axios(configBody);
    }, [version]);

    const updateProfileContent = useCallback(async () => {
        if (checkAdmin()) {
            return await axios({
                ...buildRequestConfig({
                    method: 'POST',
                    url: 'https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/content/profile',
                    data: profileContent,
                    extraHeaders: { 'Content-Type': 'application/json' },
                    extraParams: { version },
                }),
                validateStatus: (status) => status >= 200 && status <= 502,
            });
        }
        const idToken = Cookies.get('idToken');
        const requestConfig = {
            headers: {
                authorization: idToken,
                'Content-Type': 'application/json'
            },
            method: 'POST',
            url: `https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/content/profile?version=${version}`,
            validateStatus: (status) => status >= 200 && status <= 502,
            data: profileContent
        };
        return await axios(requestConfig);
    }, [version, profileContent]);

    // Fetch data on mount
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            let response = await getExistingProfileContent();

            if (response.status === 401) {
                await regenerateToken();
                response = await getExistingProfileContent();
            }

            if (response.data && response.data.isSuccess) {
                setProfileContent(response.data.data || {});
            } else {
                alert.error('Failed to load profile content');
            }
        } catch (error) {
            console.error('Error fetching profiles:', error);
            alert.error('Something went wrong while fetching data');
        } finally {
            setLoading(false);
        }
    }, [getExistingProfileContent, alert]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // =====================
    // HANDLERS (Preserved from original)
    // =====================

    // Navigation handlers
    const handleSelectProfile = (profileName) => {
        setActiveProfile(profileName);
        setActiveReportType('advanced');
        setActiveSection('about');
        setActiveLang('en');
        setCurrentView('editor');
    };

    const handleBackToDashboard = () => {
        setCurrentView('dashboard');
        setActiveProfile(null);
    };

    // Add new language globally (preserved logic)
    const addNewLang = (newLangKey) => {
        if (!newLangKey || availableLang.includes(newLangKey)) {
            alert.info('Invalid or existing language');
            return;
        }

        const newData = JSON.parse(JSON.stringify(profileContent));

        const addLanguage = (obj, langKey) => {
            if (Array.isArray(obj)) {
                obj.forEach(item => addLanguage(item, langKey));
            } else {
                for (let key in obj) {
                    if (typeof obj[key] === 'object' && obj[key] !== null) {
                        addLanguage(obj[key], langKey);
                    }
                    if (key === 'en') {
                        if (!obj.hasOwnProperty(langKey)) obj[langKey] = '';
                    }
                }
            }
        };

        addLanguage(newData, newLangKey);
        setAvailableLang([...availableLang, newLangKey]);
        setAvailableLangObj({ ...availableLangObj, [newLangKey]: '' });
        setAvailableLangArray({ ...availableLangArray, [newLangKey]: [] });
        setProfileContent(newData);
        alert.success('Successfully added new language');
    };

    // Save to API (preserved logic)
    const handleSave = async () => {
        setSaveLoading(true);
        try {
            let response = await updateProfileContent();

            if (response.status === 401) {
                await regenerateToken();
                response = await updateProfileContent();
            }

            if (response.status >= 200 && response.status < 300) {
                alert.success('Changes saved successfully!');
            } else {
                alert.error('Failed to save changes');
            }
        } catch (e) {
            console.error(e.message);
            alert.error('An error occurred while saving');
        } finally {
            setSaveLoading(false);
        }
    };

    // Content update handler (updates local state)
    const handleUpdateContent = (newContent) => {
        setProfileContent(newContent);
    };

    // Export handler
    const handleExportContent = () => {
        handleExport(profileContent);
    };

    // =====================
    // RENDER
    // =====================

    return (
        <AppLayout>
            {loading ? (
                <Loader />
            ) : (
                <div className="pfc-container">
                    {currentView === 'dashboard' ? (
                        <Dashboard
                            profileContent={profileContent}
                            onSelectProfile={handleSelectProfile}
                            onExport={handleExportContent}
                            version={version}
                            allVersions={allVersions}
                            setEditVersion={setEditVersion}
                        />
                    ) : (
                        <EditorWorkspace
                            profileContent={profileContent}
                            activeProfile={activeProfile}
                            activeReportType={activeReportType}
                            activeSection={activeSection}
                            activeLang={activeLang}
                            availableLang={availableLang}
                            availableLangObj={availableLangObj}
                            onChangeReportType={setActiveReportType}
                            onChangeSection={setActiveSection}
                            onChangeLang={setActiveLang}
                            onChangeProfile={setActiveProfile}
                            onBack={handleBackToDashboard}
                            onUpdateContent={handleUpdateContent}
                            onSave={handleSave}
                            saveLoading={saveLoading}
                        />
                    )}
                </div>
            )}
        </AppLayout>
    );
};

export default ProfileContentManager;
